import type { EventType } from '@adgf/shared';
import type { AnalyticsConfig, QueuedEvent, TrackEventOptions, FlushResult } from './types.js';
import { DEFAULT_ANALYTICS_CONFIG } from './types.js';

/**
 * EventTracker collects anonymous user behavior events and batches
 * them for delivery to the analytics endpoint.
 *
 * Design principles (M0):
 * - All user IDs are anonymous (no PII collected)
 * - Events are batched to reduce network overhead
 * - Failed sends are queued for retry (in-memory, M0)
 * - Tracker can be disabled for testing / privacy
 */
export class EventTracker {
  private config: AnalyticsConfig;
  private queue: QueuedEvent[] = [];
  private anonymousUserId: string;
  private sessionId: string;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private isFlushing = false;

  constructor(config: Partial<AnalyticsConfig> = {}) {
    this.config = { ...DEFAULT_ANALYTICS_CONFIG, ...config };
    this.anonymousUserId = this.generateAnonymousId();
    this.sessionId = this.generateSessionId();

    if (this.config.enabled && typeof window !== 'undefined') {
      this.startAutoFlush();
      window.addEventListener('beforeunload', () => this.flushSync());
    }
  }

  /**
   * Track an event.
   */
  track(eventType: EventType, options: TrackEventOptions = {}): void {
    if (!this.config.enabled) return;

    const event: QueuedEvent = {
      anonymous_user_id: this.anonymousUserId,
      game_id: options.gameId ?? null,
      session_id: this.sessionId,
      event_type: eventType,
      event_data: options.data ?? {},
      timestamp: options.timestamp ?? new Date().toISOString(),
    };

    this.queue.push(event);

    if (this.config.debug) {
      console.info('[analytics] track:', eventType, event.event_data);
    }

    if (this.queue.length >= this.config.batchSize) {
      void this.flush();
    }
  }

  /**
   * Start a new session (generates a new session ID).
   */
  startSession(): void {
    this.sessionId = this.generateSessionId();
    this.track('session_start');
  }

  /**
   * End the current session.
   */
  endSession(): void {
    this.track('session_end');
    void this.flush();
  }

  /**
   * Flush the queue to the endpoint.
   */
  async flush(): Promise<FlushResult> {
    if (this.isFlushing || this.queue.length === 0) {
      return { sent: 0, failed: 0 };
    }

    this.isFlushing = true;
    const batch = this.queue.splice(0, this.config.batchSize);

    try {
      const response = await fetch(this.config.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events: batch }),
        keepalive: true,
      });

      if (!response.ok) {
        // Re-queue failed events for retry
        this.queue.unshift(...batch);
        return { sent: 0, failed: batch.length };
      }

      return { sent: batch.length, failed: 0 };
    } catch (error) {
      this.queue.unshift(...batch);
      return {
        sent: 0,
        failed: batch.length,
        errors: [error instanceof Error ? error : new Error(String(error))],
      };
    } finally {
      this.isFlushing = false;
    }
  }

  /**
   * Synchronous flush using sendBeacon (for page unload).
   */
  flushSync(): void {
    if (this.queue.length === 0) return;

    const batch = this.queue.splice(0);
    const payload = JSON.stringify({ events: batch });

    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(this.config.endpoint, new Blob([payload], { type: 'application/json' }));
    }
  }

  /**
   * Get the current queue length.
   */
  get queueLength(): number {
    return this.queue.length;
  }

  /**
   * Get the anonymous user ID.
   */
  getAnonymousUserId(): string {
    return this.anonymousUserId;
  }

  /**
   * Get the current session ID.
   */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Set a custom anonymous user ID (e.g., from localStorage).
   */
  setAnonymousUserId(id: string): void {
    this.anonymousUserId = id;
  }

  /**
   * Enable or disable tracking.
   */
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
  }

  /**
   * Destroy the tracker and clear resources.
   */
  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    void this.flush();
  }

  private startAutoFlush(): void {
    if (this.flushTimer) return;
    this.flushTimer = setInterval(() => {
      void this.flush();
    }, this.config.flushIntervalMs);
  }

  private generateAnonymousId(): string {
    return `anon_${this.randomHex(16)}`;
  }

  private generateSessionId(): string {
    return `sess_${this.randomHex(16)}`;
  }

  private randomHex(length: number): string {
    const bytes = new Uint8Array(length);
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      crypto.getRandomValues(bytes);
    } else {
      for (let i = 0; i < length; i++) {
        bytes[i] = Math.floor(Math.random() * 256);
      }
    }
    return Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }
}
